// Domain-based branding.
// - `app.ldrakek.si` → LD Rakek branding, skip landing page, forced tenant hint.
// - `doberpogled.app` (and anything else) → generic "Dober pogled app" with public landing.
//
// Set REACT_APP_FORCE_BRAND=rakek in .env to override during local development.

const RAKEK_HOSTS = new Set([
  "app.ldrakek.si",
  "ldrakek.si",
  "www.ldrakek.si",
]);

const DOBER_HOSTS = new Set([
  "doberpogled.app",
  "www.doberpogled.app",
  "app.doberpogled.app",
  "doberpogled.si",
  "www.doberpogled.si",
  "app.doberpogled.si",
]);

const detectBrand = () => {
  const forced = (process.env.REACT_APP_FORCE_BRAND || "").toLowerCase();
  if (forced === "rakek") return "rakek";
  if (forced === "dober") return "dober";
  if (typeof window === "undefined") return "dober";
  const host = (window.location.hostname || "").toLowerCase();
  // Hostname is ALWAYS authoritative when it matches a known brand host.
  // localStorage/query-param only apply on ambiguous hosts (previews, localhost).
  if (DOBER_HOSTS.has(host)) {
    // Clean up any stale brand override so future visits are consistent.
    try { window.localStorage.removeItem("dp-brand"); } catch (_) { /* ignore */ }
    return "dober";
  }
  if (RAKEK_HOSTS.has(host)) return "rakek";
  // Ambiguous host (preview URL, localhost, custom domain) → query param + localStorage
  try {
    const qp = new URLSearchParams(window.location.search).get("brand");
    if (qp === "rakek") {
      window.localStorage.setItem("dp-brand", "rakek");
      return "rakek";
    }
    if (qp === "dober" || qp === "reset") {
      window.localStorage.removeItem("dp-brand");
      return "dober";
    }
    const stored = window.localStorage.getItem("dp-brand");
    if (stored === "rakek") return "rakek";
    if (stored === "dober") return "dober";
  } catch (_) { /* ignore */ }
  return "dober";
};

const BRANDS = {
  rakek: {
    id: "rakek",
    isRakek: true,
    isDefault: false,
    brandName: "LD Rakek",
    fullBrandName: "Lovska družina Rakek",
    tagline: "Digitalno srce naše LD",
    logoPath: "/ld-rakek-logo.png",
    faviconPath: "/ld-rakek-32.png",
    appleTouchIconPath: "/ld-rakek-180.png",
    manifestPath: "/manifest-rakek.json",
    themeColor: "#041a0e",
    showLandingPage: false,
    hideLdChooserPreset: "rakek",
    ldFilterCodes: ["rakek"],
    subscriptionUiHidden: true,
  },
  dober: {
    id: "dober",
    isRakek: false,
    isDefault: true,
    brandName: "Dober pogled app",
    fullBrandName: "Dober pogled app",
    tagline: "Središče lovske družine",
    logoPath: "/dober-pogled-logo.png",
    faviconPath: "/dober-pogled-logo.png",
    appleTouchIconPath: "/apple-touch-icon.png",
    manifestPath: "/manifest.json",
    themeColor: "#041a0e",
    showLandingPage: true,
    hideLdChooserPreset: null,
    ldFilterCodes: null,
    subscriptionUiHidden: false,
  },
};

const brandId = detectBrand();
export const BRAND = BRANDS[brandId];

// Convenience: apply document.title + meta description on mount
export const applyBrandToDocument = () => {
  try {
    if (typeof document === "undefined") return;
    document.title = BRAND.brandName;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", `${BRAND.brandName} – ${BRAND.tagline}`);
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) themeMeta.setAttribute("content", BRAND.themeColor);
    const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appleTitle) appleTitle.setAttribute("content", BRAND.brandName);
    // Dynamic favicon per brand
    const setLink = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement("link");
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };
    setLink("icon", BRAND.faviconPath);
    setLink("apple-touch-icon", BRAND.appleTouchIconPath || BRAND.faviconPath);
    setLink("shortcut icon", BRAND.faviconPath);
    // Dynamic manifest per brand (for PWA install icon on home screen)
    if (BRAND.manifestPath) {
      let manifestLink = document.querySelector('link[rel="manifest"]');
      if (!manifestLink) {
        manifestLink = document.createElement("link");
        manifestLink.rel = "manifest";
        document.head.appendChild(manifestLink);
      }
      manifestLink.href = BRAND.manifestPath;
    }
    // Rakek subdomain: prevent Google indexing (private LD)
    if (BRAND.isRakek) {
      let robots = document.querySelector('meta[name="robots"]');
      if (!robots) {
        robots = document.createElement("meta");
        robots.name = "robots";
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, nofollow");

      // iOS PWA splash screens (apple-touch-startup-image) with device-specific media queries
      // Removes any previously injected splash tags first (in case brand switches)
      document.querySelectorAll('link[data-dp-splash="1"]').forEach(el => el.remove());
      const SPLASHES = [
        { w: 640, h: 1136, dw: 320, dh: 568, r: 2 },     // iPhone SE 1st
        { w: 750, h: 1334, dw: 375, dh: 667, r: 2 },     // iPhone 6/7/8
        { w: 828, h: 1792, dw: 414, dh: 896, r: 2 },     // iPhone XR / 11
        { w: 1125, h: 2436, dw: 375, dh: 812, r: 3 },    // iPhone X / XS / 11 Pro
        { w: 1170, h: 2532, dw: 390, dh: 844, r: 3 },    // iPhone 12 / 13 / 14
        { w: 1179, h: 2556, dw: 393, dh: 852, r: 3 },    // iPhone 14 Pro / 15
        { w: 1242, h: 2208, dw: 414, dh: 736, r: 3 },    // iPhone 6+/7+/8+
        { w: 1242, h: 2688, dw: 414, dh: 896, r: 3 },    // iPhone XS Max / 11 Pro Max
        { w: 1284, h: 2778, dw: 428, dh: 926, r: 3 },    // iPhone 12/13 Pro Max
        { w: 1290, h: 2796, dw: 430, dh: 932, r: 3 },    // iPhone 14 Pro Max / 15 Pro Max
        { w: 1536, h: 2048, dw: 768, dh: 1024, r: 2 },   // iPad Mini / Air / 9.7"
        { w: 1620, h: 2160, dw: 810, dh: 1080, r: 2 },   // iPad 10.2"
        { w: 1640, h: 2360, dw: 820, dh: 1180, r: 2 },   // iPad Air 10.9"
        { w: 1668, h: 2388, dw: 834, dh: 1194, r: 2 },   // iPad Pro 11"
        { w: 2048, h: 2732, dw: 1024, dh: 1366, r: 2 },  // iPad Pro 12.9"
      ];
      SPLASHES.forEach(({ w, h, dw, dh, r }) => {
        const link = document.createElement("link");
        link.rel = "apple-touch-startup-image";
        link.setAttribute("data-dp-splash", "1");
        link.href = `/splash/ld-rakek-splash-${w}x${h}.png`;
        link.media = `(device-width: ${dw}px) and (device-height: ${dh}px) and (-webkit-device-pixel-ratio: ${r}) and (orientation: portrait)`;
        document.head.appendChild(link);
      });
    } else {
      // Non-rakek brand: clear any previously injected splash tags
      document.querySelectorAll('link[data-dp-splash="1"]').forEach(el => el.remove());
    }
  } catch (_) { /* ignore */ }
};
